import type { OnboardingStateResponseRead } from '@highlight-cp/highlighter-api';

import { MANUAL_ACTIVATION_COMMUNITY_SLUGS } from './constants';

/**
 * Fully activated highlighter — skip onboarding and send to home.
 *
 * NOTE: In-app navigation should not send these users to the onboarding route.
 */
export function isActive(data: OnboardingStateResponseRead) {
  return data.highlighter?.status === 'active';
}

/**
 * Guest highlighter with TOS already handled — no further onboarding; send to home.
 * Evaluated after {@link needsTosAgreement} so guests who must accept TOS still see that step first.
 */
export function isGuest(data: OnboardingStateResponseRead) {
  return data.highlighter?.status === 'guest';
}

/**
 * The highlighter still needs to submit personal details and has not yet verified their phone number.
 * Show the welcome screen first before collecting details.
 */
export function needsDetailsAtPhone(data: OnboardingStateResponseRead) {
  return Boolean(data.detailsRequired?.needsPhoneVerification);
}

/**
 * TOS has not been accepted yet and onboarding should require agreement before continuing.
 */
export function needsTosAgreement(data: OnboardingStateResponseRead) {
  return !data.highlighter?.tosAgreedAt;
}

/**
 * Phone is verified but DOB is missing — enter details at the dob step.
 */
export function needsDetailsAtDob(data: OnboardingStateResponseRead) {
  return Boolean(data.detailsRequired?.needsDateOfBirth);
}

/**
 * Phone is verified and DOB is collected, but address is missing — enter details at the address step.
 */
export function needsDetailsAtAddress(data: OnboardingStateResponseRead) {
  return Boolean(data.detailsRequired?.needsAddress);
}

/**
 * Phone, DOB, and address are all collected — enter details at the email step.
 */
export function needsDetailsAtEmail(data: OnboardingStateResponseRead) {
  return Boolean(data.detailsRequired?.anyRequired);
}

/**
 * The highlighter has an application survey that has been sent or started but not yet completed.
 */
export function hasApplicationInProgress(data: OnboardingStateResponseRead) {
  const applicationSurvey = data.applicationSurvey;
  return (
    !data.detailsRequired?.anyRequired &&
    Boolean(applicationSurvey) &&
    ['sent', 'started'].includes(applicationSurvey?.status ?? '')
  );
}

/**
 * The highlighter has completed their application survey, is awaiting staff approval,
 * and has no core profile surveys yet — show the "Keep Going!" / "Sit tight" card.
 * The card rendered by this view will allow highlighters to take non core profile surveys if any are present
 */
export function hasApplicationInReview(data: OnboardingStateResponseRead) {
  const applicationSurvey = data.applicationSurvey;
  return (
    !data.detailsRequired?.anyRequired &&
    ['pending response', 'completed', 'failed qc'].includes(applicationSurvey?.status ?? '') &&
    (data.coreProfile?.length ?? 0) === 0
  );
}

/**
 * The highlighter has core profile surveys to complete.
 * Covers two cases:
 *  - No application survey (e.g. custom community that skips the application step)
 *  - Application survey in a post-submission state ('pending response' or 'completed') — staff review is in progress
 */
export function needsCoreProfile(data: OnboardingStateResponseRead) {
  const applicationStatus = data.applicationSurvey?.status;
  return (
    !data.detailsRequired?.anyRequired &&
    (data.coreProfile?.length ?? 0) > 0 &&
    (!data.applicationSurvey ||
      ['pending response', 'completed', 'failed qc'].includes(applicationStatus ?? ''))
  );
}

/**
 * All prerequisites are met but the highlighter belongs to a community that
 * requires manual activation by staff (e.g. GHI). The highlighter stays in
 * "new" status until staff transitions them.
 */
export function requiresManualActivation(data: OnboardingStateResponseRead) {
  const slug = data.highlighter?.communitySlug;
  return (
    !data.detailsRequired?.anyRequired &&
    !data.applicationSurvey &&
    (data.coreProfile?.length ?? 0) === 0 &&
    Boolean(slug && MANUAL_ACTIVATION_COMMUNITY_SLUGS.includes(slug))
  );
}

/**
 * All prerequisites are met — no details required, no required surveys pending.
 * Safe to call activate.
 */
export function readyToActivate(data: OnboardingStateResponseRead) {
  const slug = data.highlighter?.communitySlug;
  return (
    !data.detailsRequired?.anyRequired &&
    !data.applicationSurvey &&
    (data.coreProfile?.length ?? 0) === 0 &&
    !MANUAL_ACTIVATION_COMMUNITY_SLUGS.includes(slug ?? '')
  );
}
